<!-- Modern Dashboard -->
<div>
  <!-- Modern Dashboard Header -->
  <div class="dashboard-header" fxLayout="column" fxLayoutGap="20px">
    <div fxLayout="row" fxLayoutAlign="space-between center">
      <div fxLayout="column">
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="12px">
          <h1 class="dashboard-title">Product Impressions</h1>
          <mat-icon class="link" 
                    (click)="copyShareableUrl()" 
                    matTooltip="Copy shareable link to clipboard"
                    matTooltipPosition="below">
            share
          </mat-icon>
        </div>
        <h2 class="company-name">{{ selectedCompany?.name || selectedCompany?.code }} • {{ getDisplayMonthYear() }}</h2>
        <h3 class="dashboard-subtitle" *ngIf="rangeFilter">
          {{ rangeFilter.isCumulative ? 'Cumulative' : 'Non Cumulative' }} Analysis
        </h3>
      </div>
    </div>
    
    <div fxLayout="row" 
         fxLayout.lt-md="column" 
         fxLayoutAlign="end center" 
         fxLayoutAlign.lt-md="stretch start" 
         fxLayoutGap="16px" 
         fxLayoutGap.lt-md="12px" 
         style="margin-right: 0;">
      <!-- Month Navigation Controls - Always stay on same row -->
      <div fxLayout="row" 
           fxLayoutAlign="stretch center" 
           fxLayoutGap="4px"
           fxFlex.lt-md="100"
           style="width: 100%;">
        <button mat-icon-button 
                (click)="navigateToPreviousMonth()"
                [disabled]="isPreviousMonthDisabled()"
                matTooltip="Previous Month"
                style="flex-shrink: 0; min-width: 40px;">
          <mat-icon>chevron_left</mat-icon>
        </button>
        
        <!-- Monthly Date Selection -->
        <mat-form-field appearance="outline" 
                        class="date-selector"
                        style="flex: 1; min-width: 0;">
          <mat-label>Select Month</mat-label>
          <input matInput 
                 [matDatepicker]="monthPicker" 
                 [(ngModel)]="selectedMonthDate"
                 [min]="minDate"
                 [max]="maxDate"
                 readonly>
          <mat-datepicker-toggle matSuffix [for]="monthPicker"></mat-datepicker-toggle>
          <mat-datepicker #monthPicker 
                          startView="multi-year" 
                          (monthSelected)="setMonthAndYear($event, monthPicker)">
          </mat-datepicker>
        </mat-form-field>
        
        <button mat-icon-button 
                (click)="navigateToNextMonth()"
                [disabled]="isNextMonthDisabled()"
                matTooltip="Next Month"
                style="flex-shrink: 0; min-width: 40px;">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
      
      <!-- Export Button -->
      <button mat-raised-button 
              color="primary" 
              fxFlex.lt-md="100"
              id="canvasToPNG" 
              (click)="saveChartAsPNG()" 
              download='filename.png' 
              style="margin-right: 0;">
        <mat-icon>image</mat-icon>
        Export PNG
      </button>
    </div>
  </div>

  <!-- Dashboard Content -->
  <div class="dashboard-content" fxLayout="column" fxLayoutGap="16px">
    
    <!-- Key Metrics Row at Top -->
    <div class="metrics-sidebar">
      <div fxLayout="row wrap" 
           fxLayout.lt-md="column" 
           fxLayoutGap="16px"
           fxLayoutGap.lt-md="12px">
        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100" *ngIf="!deepLinked">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(60, 80%, 55%)'">{{ (averageForTime || 0) | number }}</div>
            <div class="metric-label">{{ averageTag || 'Daily' }} Average</div>
            <div class="metric-subtitle">Per time period</div>
          </mat-card-content>
        </mat-card>
        
        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(145, 60%, 55%)'">{{ (billImpressionCount || 0) | number }}</div>
            <div class="metric-label">Billable</div>
            <div class="metric-subtitle">Revenue generating</div>
          </mat-card-content>
        </mat-card>
        
        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(330, 90%, 55%)'">{{ ((totalImpressionCount || 0) - (billImpressionCount || 0)) | number }}</div>
            <div class="metric-label">Non-Billable</div>
            <div class="metric-subtitle">Free usage</div>
          </mat-card-content>
        </mat-card>
        
        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(210, 90%, 55%)'">{{ (totalImpressionCount || 0) | number }}</div>
            <div class="metric-label">Total Impressions</div>
            <div class="metric-subtitle">All time period</div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>


    <!-- Charts Row -->
    <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px">
      <!-- Regular Chart -->
      <mat-card class="chart-card" fxFlex="50" fxFlex.lt-md="100">
        <mat-card-header>
          <mat-card-title [style.color]="'hsl(190, 70%, 55%)'">Daily Impressions</mat-card-title>
          <mat-card-subtitle>Daily breakdown for selected month</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div *ngIf="!loading && rawData && rawData.length > 0" class="chart-container">
            <canvas id="canvas" baseChart [datasets]="barChartData" [labels]="barChartLabels" [options]="barChartOptions"
              [legend]="barChartLegend" [chartType]="barChartType" [colors]="chartColors"></canvas>
          </div>
          <div *ngIf="loading" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-spinner diameter="60"></mat-spinner>
            <p style="margin-top: 16px; color: var(--text-secondary);">Loading chart data...</p>
          </div>
          <div *ngIf="!loading && (!rawData || rawData.length === 0)" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-icon style="font-size: 48px; color: var(--text-secondary);">bar_chart</mat-icon>
            <p style="margin-top: 16px; color: var(--text-secondary);">No data available for the selected period.</p>
            <p style="color: var(--text-secondary); font-size: 14px;">Try selecting a different date range or check your connection.</p>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Cumulative Chart -->
      <mat-card class="chart-card" fxFlex="50" fxFlex.lt-md="100">
        <mat-card-header>
          <mat-card-title [style.color]="'hsl(280, 70%, 55%)'">Cumulative Impressions</mat-card-title>
          <mat-card-subtitle>Running total for selected month</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div *ngIf="!loading && rawData && rawData.length > 0" class="chart-container">
            <canvas id="cumulativeCanvas" baseChart [datasets]="cumulativeChartData" [labels]="barChartLabels" [options]="barChartOptions"
              [legend]="barChartLegend" [chartType]="barChartType" [colors]="chartColors"></canvas>
          </div>
          <div *ngIf="loading" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-spinner diameter="60"></mat-spinner>
            <p style="margin-top: 16px; color: var(--text-secondary);">Loading chart data...</p>
          </div>
          <div *ngIf="!loading && (!rawData || rawData.length === 0)" class="loading-state" fxLayout="column" fxLayoutAlign="center center">
            <mat-icon style="font-size: 48px; color: var(--text-secondary);">bar_chart</mat-icon>
            <p style="margin-top: 16px; color: var(--text-secondary);">No data available for the selected period.</p>
            <p style="color: var(--text-secondary); font-size: 14px;">Try selecting a different date range or check your connection.</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</div>